import React, { useState } from "react";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { submitQuote } from "@/lib/api";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const FACILITY_TYPES = [
  "Medical Office","Dialysis Center","Professional Office","Government Facility",
  "Retail Store","Industrial Facility","Educational Facility","Church",
  "Property Management","HOA or Clubhouse","Multi-Tenant Building","Other",
];
const SQFT = [
  "Under 2,500 sq. ft.","2,500 – 5,000 sq. ft.","5,000 – 10,000 sq. ft.",
  "10,000 – 25,000 sq. ft.","Over 25,000 sq. ft.","Not Sure",
];
const LOCATIONS = ["One","Two to Five","Six or More"];
const SERVICES_LIST = [
  "Routine Janitorial Services","Medical Facility Cleaning","Day Porter Services","Carpet Cleaning",
  "Floor Scrubbing","Strip and Wax","Tile and Grout Cleaning","Window Cleaning",
  "Pressure Washing","Post-Construction Cleanup","Disinfection Services","Emergency Cleanup","Other",
];
const FREQUENCIES = ["Daily","Multiple Times Per Week","Weekly","Biweekly","Monthly","Quarterly","One-Time Service","Not Sure"];
const CHALLENGES = [
  "Inconsistent Quality","Missed Cleanings","Poor Communication","Staffing Issues",
  "Lack of Supervision","Looking for Better Service","Contract Ending Soon","Opening a New Facility","No Current Provider","Other",
];
const CONTACT_METHODS = ["Phone","Email","Either"];
const BEST_TIMES = ["Morning","Afternoon","Evening","Anytime"];

const INITIAL = {
  name: "", company: "", job_title: "", email: "", phone: "",
  facility_name: "", facility_address: "", city: "", state: "", zip_code: "",
  facility_type: "", square_footage: "", num_locations: "",
  services: [], frequency: "", current_provider: "",
  challenges: [], message: "", start_date: "", preferred_contact_method: "",
  best_time_to_contact: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleIn = (k, v) =>
    setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.phone) return toast.error("Please complete name, company, email, and phone.");
    if (!form.facility_address || !form.city || !form.state || !form.zip_code || !form.facility_type || !form.square_footage) {
      return toast.error("Please complete facility address, city, state, ZIP, facility type, and square footage.");
    }
    setBusy(true);
    try {
      await submitQuote(form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Facility assessment requested. We'll be in touch within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again or call us directly.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success-message" className="bg-white border border-[var(--es-border)] rounded-sm p-10 md:p-14 text-center">
        <CheckCircle size={64} weight="fill" className="mx-auto text-[var(--es-accent)]" />
        <h2 className="mt-6 text-3xl font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Thank you for contacting Empire Serv.</h2>
        <p className="mt-4 text-[var(--es-text-muted)] max-w-xl mx-auto">
          Your request has been received. A member of our team will contact you within one business day to learn more about your facility and service needs.
        </p>
        <p className="mt-6 text-sm uppercase tracking-[0.25em] font-semibold text-[var(--es-accent)]">
          Be the Solution · Be Excellent · Be Reliable · Be Valuable
        </p>
      </div>
    );
  }

  return (
    <form data-testid="quote-form" onSubmit={onSubmit} className="bg-white border border-[var(--es-border)] rounded-sm p-6 md:p-10 space-y-10">
      <Section title="Contact Information" number="1">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Full Name *"><input required className="es-input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="quote-input-name" /></Field>
          <Field label="Company Name *"><input required className="es-input" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="quote-input-company" /></Field>
          <Field label="Job Title"><input className="es-input" value={form.job_title} onChange={(e) => set("job_title", e.target.value)} data-testid="quote-input-job-title" /></Field>
          <Field label="Email Address *"><input required type="email" className="es-input" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="quote-input-email" /></Field>
          <Field label="Phone Number *"><input required type="tel" className="es-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="quote-input-phone" /></Field>
        </div>
      </Section>

      <Section title="Facility Information" number="2">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Facility Name"><input className="es-input" value={form.facility_name} onChange={(e) => set("facility_name", e.target.value)} data-testid="quote-input-facility-name" /></Field>
          <Field label="Facility Address *"><input required className="es-input" value={form.facility_address} onChange={(e) => set("facility_address", e.target.value)} data-testid="quote-input-facility-address" /></Field>
          <Field label="City *"><input required className="es-input" value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="quote-input-city" /></Field>
          <Field label="State *">
            <select required className="es-input" value={form.state} onChange={(e) => set("state", e.target.value)} data-testid="quote-input-state">
              <option value="">Select…</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="ZIP Code *"><input required className="es-input" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} data-testid="quote-input-zip" /></Field>
          <Field label="Facility Type *">
            <select required className="es-input" value={form.facility_type} onChange={(e) => set("facility_type", e.target.value)} data-testid="quote-input-facility-type">
              <option value="">Select…</option>
              {FACILITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Approximate Square Footage *">
            <select required className="es-input" value={form.square_footage} onChange={(e) => set("square_footage", e.target.value)} data-testid="quote-input-sqft">
              <option value="">Select…</option>
              {SQFT.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Number of Locations">
            <select className="es-input" value={form.num_locations} onChange={(e) => set("num_locations", e.target.value)} data-testid="quote-input-locations">
              <option value="">Select…</option>
              {LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </Field>
        </div>
      </Section>

      <Section title="Services Needed" number="3">
        <CheckGrid items={SERVICES_LIST} selected={form.services} onToggle={(v) => toggleIn("services", v)} testPrefix="quote-service" cols="grid-cols-2 md:grid-cols-3" />

        <div className="mt-5 grid md:grid-cols-2 gap-5">
          <div>
            <div className="es-label">Service Frequency</div>
            <RadioGrid items={FREQUENCIES} selected={form.frequency} onSelect={(v) => set("frequency", v)} testPrefix="quote-frequency" cols="grid-cols-1" />
          </div>
          <div>
            <div className="es-label">Do you currently have a cleaning provider?</div>
            <RadioGrid items={["Yes","No"]} selected={form.current_provider} onSelect={(v) => set("current_provider", v)} testPrefix="quote-provider" cols="grid-cols-2" />
          </div>
        </div>
      </Section>

      <Section title="Current Cleaning Situation" number="4">
        <div className="es-label">What challenges are you experiencing?</div>
        <CheckGrid items={CHALLENGES} selected={form.challenges} onToggle={(v) => toggleIn("challenges", v)} testPrefix="quote-challenge" cols="grid-cols-2 md:grid-cols-3" />

        <div className="mt-5">
          <Field label="Tell us about your facility and cleaning needs.">
            <textarea rows={5} className="es-input" value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="quote-input-message" />
          </Field>
        </div>
      </Section>

      <Section title="Scheduling" number="5">
        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Preferred Start Date">
            <input type="date" className="es-input" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="quote-input-start-date" />
          </Field>
          <Field label="Best Time to Contact You">
            <select className="es-input" value={form.best_time_to_contact} onChange={(e) => set("best_time_to_contact", e.target.value)} data-testid="quote-input-best-time">
              <option value="">Select…</option>
              {BEST_TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
        </div>
        <div className="mt-5">
          <div className="es-label">Preferred Contact Method</div>
          <RadioGrid items={CONTACT_METHODS} selected={form.preferred_contact_method} onSelect={(v) => set("preferred_contact_method", v)} testPrefix="quote-contact-method" cols="grid-cols-3" />
        </div>
      </Section>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-[var(--es-border)]">
        <p className="text-xs text-[var(--es-text-muted)] max-w-md">
          By submitting, you consent to be contacted by Empire Serv about your inquiry. We respond within one business day.
        </p>
        <button type="submit" disabled={busy} data-testid="quote-form-submit-button" className="es-btn-accent whitespace-nowrap disabled:opacity-50">
          {busy ? "Submitting…" : "Schedule My Free Facility Assessment"} <ArrowRight size={18} />
        </button>
      </div>
    </form>
  );
}

function Section({ title, number, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <span className="w-8 h-8 bg-[var(--es-navy)] text-[var(--es-accent)] rounded-sm flex items-center justify-center font-bold text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{number}</span>
        <h3 className="text-xl font-bold text-[var(--es-navy)]" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{title}</h3>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="es-label">{label}</span>
      {children}
    </label>
  );
}

function CheckGrid({ items, selected, onToggle, testPrefix, cols }) {
  return (
    <div className={`grid ${cols} gap-2 mt-2`}>
      {items.map((v) => {
        const on = selected.includes(v);
        const id = `${testPrefix}-${v.replace(/\W+/g, "-").toLowerCase()}`;
        return (
          <button type="button" key={v} data-testid={id} onClick={() => onToggle(v)}
            className={`text-xs md:text-sm text-left border px-3 py-2.5 rounded-sm transition-colors flex items-center gap-2 ${on ? "border-[var(--es-navy)] bg-[var(--es-navy)] text-white" : "border-[var(--es-border)] bg-white text-[var(--es-text)] hover:border-[var(--es-navy)]"}`}>
            <span className={`w-3.5 h-3.5 border rounded-[2px] flex items-center justify-center flex-shrink-0 ${on ? "bg-[var(--es-accent)] border-[var(--es-accent)]" : "border-[var(--es-border)]"}`}>
              {on && <CheckCircle size={10} weight="fill" className="text-[var(--es-navy)]" />}
            </span>
            {v}
          </button>
        );
      })}
    </div>
  );
}

function RadioGrid({ items, selected, onSelect, testPrefix, cols }) {
  return (
    <div className={`grid ${cols} gap-2 mt-2`}>
      {items.map((v) => {
        const on = selected === v;
        const id = `${testPrefix}-${v.replace(/\W+/g, "-").toLowerCase()}`;
        return (
          <button type="button" key={v} data-testid={id} onClick={() => onSelect(v)}
            className={`text-xs md:text-sm text-left border px-3 py-2.5 rounded-sm transition-colors flex items-center gap-2 ${on ? "border-[var(--es-navy)] bg-[var(--es-navy)] text-white" : "border-[var(--es-border)] bg-white text-[var(--es-text)] hover:border-[var(--es-navy)]"}`}>
            <span className={`w-3.5 h-3.5 border rounded-full flex items-center justify-center flex-shrink-0 ${on ? "border-[var(--es-accent)]" : "border-[var(--es-border)]"}`}>
              {on && <span className="w-1.5 h-1.5 bg-[var(--es-accent)] rounded-full" />}
            </span>
            {v}
          </button>
        );
      })}
    </div>
  );
}
